import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mic, Square, Loader2, ArrowLeft, Sparkles, Trash2, Calendar } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

type Journal = {
  id: string;
  transcript: string;
  mood: string | null;
  themes: string[] | null;
  key_insights: string[] | null;
  summary: string | null;
  duration_seconds: number | null;
  entry_date: string;
  created_at: string;
};

type WeeklyInsights = {
  dominant_mood?: string;
  recurring_themes?: string[];
  patterns?: string[];
  wins?: string[];
  growth_focus?: string;
  affirmation?: string;
};

const MOOD_STYLES: Record<string, string> = {
  happy: "bg-yellow-500/10 text-yellow-500",
  motivated: "bg-emerald-500/10 text-emerald-500",
  calm: "bg-blue-500/10 text-blue-500",
  neutral: "bg-muted text-muted-foreground",
  reflective: "bg-purple-500/10 text-purple-500",
  anxious: "bg-amber-500/10 text-amber-500",
  sad: "bg-slate-500/10 text-slate-400",
  frustrated: "bg-red-500/10 text-red-500",
};

const VoiceJournal = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<Journal[]>([]);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [insights, setInsights] = useState<WeeklyInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);

  const load = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("voice_journals")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);
    setEntries((data as Journal[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        const seconds = (Date.now() - startedAtRef.current) / 1000;
        await uploadBlob(blob, mime || "audio/webm", seconds);
      };
      mediaRecorderRef.current = rec;
      rec.start();
      startedAtRef.current = Date.now();
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);
      setRecording(true);
    } catch (e) {
      console.error(e);
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current) return;
    mediaRecorderRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    setProcessing(true);
  };

  const uploadBlob = async (blob: Blob, mime: string, seconds: number) => {
    try {
      const format =
        mime.includes("mp4") || mime.includes("m4a") ? "m4a" : "webm";
      const buf = await blob.arrayBuffer();
      const bytes = new Uint8Array(buf);
      // Chunked base64 to avoid stack overflow on long recordings
      let bin = "";
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(
          null,
          Array.from(bytes.subarray(i, i + CHUNK)) as unknown as number[]
        );
      }
      const base64 = btoa(bin);

      const { data, error } = await supabase.functions.invoke("transcribe-journal", {
        body: { audioBase64: base64, format, durationSeconds: seconds },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      toast.success("Journal saved");
      await load();
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to process recording");
    } finally {
      setProcessing(false);
      setElapsed(0);
    }
  };

  const removeEntry = async (id: string) => {
    const { error } = await supabase.from("voice_journals").delete().eq("id", id);
    if (error) return toast.error("Failed to delete");
    setEntries((prev) => prev.filter((e) => e.id !== id));
    toast.success("Entry deleted");
  };

  const getWeeklyInsights = async () => {
    setInsightsLoading(true);
    setInsights(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "journal-weekly-insights",
        { body: {} }
      );
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      if (!(data as any)?.insights) {
        toast.message((data as any)?.message || "No entries this week yet");
      } else {
        setInsights((data as any).insights);
      }
    } catch (e: any) {
      toast.error(e.message || "Failed to generate insights");
    } finally {
      setInsightsLoading(false);
    }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <WeKitLogo size="sm" />
            <span className="font-display text-lg font-bold">WeKIT™</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 sm:px-6 py-8 space-y-8">
        <section>
          <p className="text-sm text-muted-foreground">Voice Journal</p>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-bold">
            Talk it out. <span className="gradient-text">YOMA listens.</span>
          </h1>
          <p className="mt-2 text-muted-foreground">
            Record short voice notes about your day. We transcribe, surface patterns, and reflect them back weekly.
          </p>
        </section>

        {/* Recorder */}
        <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-accent/5">
          <CardContent className="p-8 flex flex-col items-center gap-4">
            <button
              onClick={recording ? stopRecording : startRecording}
              disabled={processing}
              className={`h-24 w-24 rounded-full flex items-center justify-center transition-all shadow-lg ${
                recording
                  ? "bg-red-500 hover:bg-red-600 animate-pulse"
                  : "bg-primary hover:bg-primary/90 hover-glow"
              } disabled:opacity-60`}
              aria-label={recording ? "Stop recording" : "Start recording"}
            >
              {processing ? (
                <Loader2 className="h-10 w-10 text-primary-foreground animate-spin" />
              ) : recording ? (
                <Square className="h-10 w-10 text-white" />
              ) : (
                <Mic className="h-10 w-10 text-primary-foreground" />
              )}
            </button>
            <div className="text-center">
              <p className="font-display text-2xl font-bold tabular-nums">
                {recording ? `${mm}:${ss}` : processing ? "Transcribing…" : "Tap to record"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {recording ? "Speak naturally. Tap again to finish." : "Aim for 30s–3min per entry."}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Weekly insights */}
        <Card className="border-border/50">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> Weekly Insights
            </CardTitle>
            <Button size="sm" variant="outline" onClick={getWeeklyInsights} disabled={insightsLoading}>
              {insightsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Generate"}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {!insights && !insightsLoading && (
              <p className="text-sm text-muted-foreground">
                See your dominant mood, recurring themes, wins, and a growth focus for next week.
              </p>
            )}
            {insights && (
              <div className="space-y-4 text-sm">
                <div className="flex flex-wrap gap-2">
                  {insights.dominant_mood && (
                    <Badge className={`${MOOD_STYLES[insights.dominant_mood] || "bg-muted"} border-0`}>
                      Mood: {insights.dominant_mood}
                    </Badge>
                  )}
                  {insights.recurring_themes?.map((t) => (
                    <Badge key={t} variant="outline">{t}</Badge>
                  ))}
                </div>
                {insights.patterns && insights.patterns.length > 0 && (
                  <div>
                    <p className="font-semibold mb-1">Patterns</p>
                    <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                      {insights.patterns.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                )}
                {insights.wins && insights.wins.length > 0 && (
                  <div>
                    <p className="font-semibold mb-1">Wins</p>
                    <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                      {insights.wins.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                )}
                {insights.growth_focus && (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <p className="text-xs uppercase tracking-wide text-primary font-semibold">Next week</p>
                    <p className="mt-1">{insights.growth_focus}</p>
                  </div>
                )}
                {insights.affirmation && (
                  <p className="italic text-center text-muted-foreground">"{insights.affirmation}"</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Entries */}
        <section>
          <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" /> Recent entries
          </h2>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : entries.length === 0 ? (
            <Card className="border-border/50">
              <CardContent className="p-8 text-center text-muted-foreground text-sm">
                No entries yet. Record your first thought above.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {entries.map((e) => (
                <Card key={e.id} className="border-border/50">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {e.mood && (
                          <Badge className={`${MOOD_STYLES[e.mood] || "bg-muted"} border-0 text-xs`}>
                            {e.mood}
                          </Badge>
                        )}
                        {e.themes?.map((t) => (
                          <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                        ))}
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(e.created_at), { addSuffix: true })}
                          {e.duration_seconds ? ` · ${e.duration_seconds}s` : ""}
                        </span>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => removeEntry(e.id)} aria-label="Delete">
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                    {e.summary && <p className="text-sm font-medium">{e.summary}</p>}
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{e.transcript}</p>
                    {e.key_insights && e.key_insights.length > 0 && (
                      <div className="rounded-lg border border-border/50 bg-muted/30 p-3 space-y-1">
                        <p className="text-xs uppercase tracking-wide text-primary font-semibold">YOMA reflects</p>
                        <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-0.5">
                          {e.key_insights.map((k, i) => <li key={i}>{k}</li>)}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default VoiceJournal;
